import { faCrosshairs } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSpring } from '@react-spring/web';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { useEffect, useMemo, useRef } from 'react';

import { useOrders } from '@hooks/useOrders';
import {
  usePositionAggregation,
  type AggregatedPosition,
} from '@hooks/usePositionAggregation';
import type { Order } from '@services/orderTypes';
import { getCurrentTime } from '@services/timeshiftService';
import type { Asset } from '@shared/types';

import { InteractionOverlay } from './InteractionOverlay';
import { OrderMarkers } from './OrderMarkers';
import { PositionMarkers } from './PositionMarkers';
import {
  ChartAnimations,
  CurrentPriceCircle,
  GridLines,
  PriceLine,
  TimeRangeIndicator,
} from './PriceVisualization';
import { AllStrikePrices, StrikePriceLine } from './StrikePriceComponents';
import { TradeZones } from './TradeZoneComponents';
import { DataPoint, DEFAULT_MARGIN } from './types';
import { useChartEffects } from './useChartEffects';
import { useChartState } from './useChartState';

interface ChartProps {
  spotPrice: number;
  priceDirection: 'up' | 'down';
  strikePrice?: number;
  strikePrices?: number[];
  asset: Asset;
  priceHistory: DataPoint[];
  isLoading: boolean;
  isReady: boolean;
  onStrikePriceSelect?: (price: number) => void;
  onTradeZoneSelect?: (tradeType: 'CALL' | 'PUT') => void;
  onTradeZoneDeselect?: () => void;
  selectedTradeType?: 'CALL' | 'PUT' | null;
  selectedPositionGroup?: AggregatedPosition | null;
  onPositionSelect?: (position: AggregatedPosition | null) => void;
  selectedOrder?: Order | null;
  onOrderSelect?: (order: Order | null) => void;
  onManageOrders?: (orders: Order[], strikePrice: number) => void;
  isManageOrdersPanelOpen?: boolean;
}

export function VisxChart({
  spotPrice,
  priceDirection,
  strikePrice,
  strikePrices = [],
  asset,
  priceHistory,
  isLoading,
  isReady,
  onStrikePriceSelect,
  onTradeZoneSelect,
  onTradeZoneDeselect,
  selectedTradeType,
  selectedPositionGroup,
  onPositionSelect,
  selectedOrder,
  onOrderSelect,
  onManageOrders,
  isManageOrdersPanelOpen = false,
}: ChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Get aggregated positions for this asset
  const aggregatedPositions = usePositionAggregation(asset, {
    spotPrice,
    isReady,
  });

  // Get open orders for this asset
  const { openOrders } = useOrders();

  // Filter orders for the selected strike
  const ordersForStrike = useMemo(() => {
    if (!strikePrice) {
      return [];
    }
    return openOrders.filter(
      order =>
        order.asset === asset &&
        order.strikePrice === strikePrice &&
        order.status === 'PENDING',
    );
  }, [openOrders, asset, strikePrice]);

  // Use custom hooks for state management (before destructuring)
  const chartState = useChartState({
    spotPrice,
    strikePrice,
    asset,
    priceHistory,
    strikePrices,
  });

  const {
    dimensions,
    setDimensions,
    xFrom,
    createXScale,
    yScale,
    previewStrikePrice,
    previewRawPrice: _previewRawPrice,
    nearestValidStrike,
    smoothedCenterPrice,
    smoothedSpotPrice: _smoothedSpotPrice,
    smoothedYBounds,
    isInitialized,
    setIsInitialized,
    lastTouchDistance,
    lastUpdateTime,
    isDraggingStrike,
    yScaleRef,
    marginRef,
    touchStartTime,
    updatePreviewStrike,
    clearPreviewStrike,
    handleXFromChange,
    handleYZoomChange,
    handlePan,
    recenterView,
    hasManuallyPanned,
  } = chartState;

  // Color animation for price direction
  const colorSpring = useSpring({
    color: priceDirection === 'up' ? 'var(--brand-green)' : 'var(--brand-red)',
    config: { tension: 200, friction: 35 },
  });

  // Container resize effect
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        const newDimensions = {
          width: width > 0 ? width : 390,
          height: height > 0 ? height : 800,
        };
        setDimensions(newDimensions);
        // No need to update xScale - it will be recreated with new dimensions automatically
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateDimensions);
      resizeObserver.disconnect();
    };
  }, []);

  // Use chart effects hook for all complex effects
  useChartEffects({
    spotPrice,
    strikePrice,
    priceHistory,
    isInitialized,
    setIsInitialized,
    smoothedCenterPrice,
    smoothedYBounds,
    yScale,
    lastAutoScaleStrike: chartState.lastAutoScaleStrike,
    isDraggingStrike,
    strikePrices,
    onStrikePriceSelect,
    previewStrikePrice,
    nearestValidStrike,
    setSmoothedCenterPrice: chartState.setSmoothedCenterPrice,
    setSmoothedSpotPrice: chartState.setSmoothedSpotPrice,
    setSmoothedYBounds: chartState.setSmoothedYBounds,
    setYScale: chartState.setYScale,
    setLastAutoScaleStrike: chartState.setLastAutoScaleStrike,
    savedView: chartState.savedView,
    setSavedView: chartState.setSavedView,
    updatePreviewStrike,
    clearPreviewStrike,
    containerRef,
    yScaleRef,
    marginRef,
  });

  // Reinitialize when asset changes
  useEffect(() => {
    chartState.setSmoothedCenterPrice(0);
    chartState.setSmoothedSpotPrice(0);
    setIsInitialized(false);
  }, [asset]);

  // Intelligent resolution reduction based on xFrom
  const consolidatedPriceHistory = useMemo(() => {
    if (priceHistory.length === 0) {
      return [];
    }

    // Get the actual visible time range
    const currentTime = Date.now();
    const visibleTimeSpan = currentTime - xFrom;

    // Determine consolidation interval based on visible time span
    let consolidationMs: number;
    if (visibleTimeSpan <= 10 * 60 * 1000) {
      // 15 minutes or less - show every second (no consolidation)
      consolidationMs = 0;
    } else if (visibleTimeSpan <= 30 * 60 * 1000) {
      // 6 hours or less - consolidate to 1-minute intervals
      consolidationMs = 5 * 1000;
    } else if (visibleTimeSpan <= 2 * 60 * 60 * 1000) {
      // 6 hours or less - consolidate to 1-minute intervals
      consolidationMs = 15 * 1000;
    } else if (visibleTimeSpan <= 6 * 60 * 60 * 1000) {
      // 6 hours or less - consolidate to 1-minute intervals
      consolidationMs = 60 * 1000;
    } else {
      // More than 6 hours - consolidate to 5-minute intervals
      consolidationMs = 5 * 60 * 1000;
    }

    // If no consolidation needed, return original data
    let consolidated: DataPoint[] = priceHistory;

    if (consolidationMs !== 0) {
      // Consolidate data points
      consolidated = [];
      const buckets = new Map<number, DataPoint[]>();

      // Group data points into time buckets
      for (const point of priceHistory) {
        const bucketKey =
          Math.floor(point.timestamp / consolidationMs) * consolidationMs;
        if (!buckets.has(bucketKey)) {
          buckets.set(bucketKey, []);
        }
        buckets.get(bucketKey)!.push(point);
      }

      // Create consolidated points (using last price in each bucket for consistency)
      for (const [bucketTime, points] of buckets.entries()) {
        if (points.length > 0) {
          // Use the last point in the bucket (most recent price)
          const lastPoint = points[points.length - 1];
          consolidated.push({
            timestamp: bucketTime + consolidationMs / 2, // Center timestamp in bucket
            price: lastPoint.price,
          });
        }
      }

      // Sort by timestamp to ensure proper ordering
      consolidated.sort((a, b) => a.timestamp - b.timestamp);
    }

    // Filter to only include points within the visible time range
    const visibleStart = xFrom;
    const startIdx = consolidated.findIndex(
      point => point.timestamp >= visibleStart,
    );
    const filtered =
      startIdx === -1 ? [] : consolidated.slice(Math.max(startIdx - 1, 0));

    return filtered;
  }, [priceHistory, xFrom]);

  // Loading state - use external ready state from data hook
  if (isLoading || priceHistory.length === 0 || !isReady) {
    return (
      <div
        ref={containerRef}
        className="flex items-center justify-center size-full"
      >
        <div className="text-center text-[#808080] pt-24">
          <div className="animate-pulse text-[16px] mb-2">
            Loading real-time data...
          </div>
          <div className="text-[12px] opacity-60">
            Collecting price history...
          </div>
        </div>
      </div>
    );
  }

  const { width, height } = dimensions;
  const margin = DEFAULT_MARGIN;
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  if (innerWidth <= 0 || innerHeight <= 0) {
    return <div ref={containerRef} className="absolute inset-0" />;
  }
  // Use consolidated and filtered price history with current price for latest entry
  // Use timeshifted time to align with historical data
  const currentTime = getCurrentTime();
  const allData: DataPoint[] =
    consolidatedPriceHistory.length > 0
      ? [
          ...consolidatedPriceHistory, // All consolidated data
          { timestamp: currentTime, price: spotPrice }, // Use actual spot price for current position
        ]
      : [{ timestamp: currentTime, price: spotPrice }]; // Use actual spot price for current position

  // Create xScale for rendering with consistent currentTime
  const xScale = createXScale(currentTime);

  // Use yScale directly for price bounds
  const yScaleVisx = scaleLinear({
    domain: yScale,
    range: [innerHeight, 0],
  });

  // Update refs for global handlers
  yScaleRef.current = yScaleVisx;
  marginRef.current = margin;

  // Calculate current price circle position
  const lastDataPoint = allData[allData.length - 1];
  const circleX = lastDataPoint ? xScale(lastDataPoint.timestamp) : 0;
  const circleY = lastDataPoint ? yScaleVisx(lastDataPoint.price) : 0;

  // Calculate time range for display
  const timeRangeMs = currentTime - xFrom;

  return (
    <div
      ref={containerRef}
      className="absolute inset-0"
      style={{
        overscrollBehavior: 'none',
        ...({
          WebkitOverscrollBehavior: 'none',
        } as any),
      }}
    >
      <svg width={width} height={height}>
        <ChartAnimations>
          <Group left={0} top={margin.top}>
            {/* Position markers for aggregated positions - behind price line */}
            <PositionMarkers
              aggregatedPositions={aggregatedPositions}
              yScale={yScaleVisx}
              width={width}
              margin={margin}
              selectedPositionGroup={selectedPositionGroup}
              onPositionSelect={onPositionSelect}
            />

            {/* Order markers for open orders - dashed lines */}
            <OrderMarkers
              asset={asset}
              yScale={yScaleVisx}
              width={width}
              margin={margin}
              selectedOrder={selectedOrder}
              onOrderSelect={onOrderSelect}
            />

            {/* All strike prices with call/put indicators - always visible */}
            <AllStrikePrices
              strikePrices={strikePrices}
              selectedStrike={strikePrice}
              yScale={yScaleVisx}
              centerX={innerWidth / 2}
              spotPrice={spotPrice}
              asset={asset}
              isVisible={true}
              isDraggingStrike={isDraggingStrike.current}
              innerHeight={innerHeight}
            />

            {/* Price labels - disabled since we now have centered labels in grid lines */}

            {/* Interaction overlay for zoom and strike selection */}
            <InteractionOverlay
              innerWidth={innerWidth}
              innerHeight={innerHeight}
              margin={margin}
              yScale={yScaleVisx}
              strikePrices={strikePrices}
              strikePrice={strikePrice}
              isDraggingStrike={isDraggingStrike.current}
              touchStartTime={touchStartTime}
              lastTouchDistance={lastTouchDistance}
              lastUpdateTime={lastUpdateTime}
              containerRef={containerRef as React.RefObject<HTMLDivElement>}
              aggregatedPositions={aggregatedPositions}
              selectedPositionGroup={selectedPositionGroup}
              onStrikePriceSelect={onStrikePriceSelect}
              onPositionSelect={onPositionSelect}
              onStrikeDragUpdate={updatePreviewStrike}
              onStrikeDragEnd={() => {
                // Drag end is handled by global event listeners in useChartEffects
                // This is just a fallback for edge cases
              }}
              onXScaleChange={handleXFromChange}
              onYZoomChange={handleYZoomChange}
              onPan={handlePan}
              onTradeZoneDeselect={onTradeZoneDeselect}
            />

            {/* Colored zones when strike is selected - rendered before strike pill */}
            <TradeZones
              strikePrice={strikePrice ?? 0}
              yScale={yScaleVisx}
              innerWidth={innerWidth}
              innerHeight={innerHeight}
              margin={margin}
              selectedTradeType={selectedTradeType}
              onTradeZoneSelect={onTradeZoneSelect}
              onTradeZoneDeselect={onTradeZoneDeselect}
              isDraggingStrike={isDraggingStrike.current}
              previewStrikePrice={previewStrikePrice}
            />

            {/* Price line - rendered first so strikes appear on top */}
            <PriceLine
              data={allData}
              xScale={xScale}
              yScale={yScaleVisx}
              color={colorSpring.color.get()}
            />
            {/* Grid line price labels - render after price line */}
            <GridLines
              strikePrices={strikePrices}
              yScale={yScaleVisx}
              innerWidth={innerWidth}
              innerHeight={innerHeight}
              yScaleDomain={yScale}
              labelsOnly={true}
              selectedStrike={strikePrice}
              spotPrice={spotPrice}
              asset={asset}
              isDraggingStrike={isDraggingStrike.current}
              nearestValidStrike={nearestValidStrike}
            />

            {/* Grid lines using strike prices - with centered labels */}
            <GridLines
              strikePrices={strikePrices}
              yScale={yScaleVisx}
              innerWidth={innerWidth}
              innerHeight={innerHeight}
              yScaleDomain={yScale}
              selectedStrike={strikePrice}
              spotPrice={spotPrice}
              asset={asset}
              isDraggingStrike={isDraggingStrike.current}
              nearestValidStrike={nearestValidStrike}
            />

            {/* Strike price line - draggable (rendered above price line) */}
            <StrikePriceLine
              strikePrice={strikePrice ?? 0}
              yScale={yScaleVisx}
              innerWidth={innerWidth}
              isDraggingStrike={isDraggingStrike.current}
              previewStrikePrice={previewStrikePrice}
              nearestValidStrike={nearestValidStrike}
              spotPrice={spotPrice}
              asset={asset}
              onStrikePriceSelect={onStrikePriceSelect ?? (() => {})}
              onStrikeDragStart={() => {
                isDraggingStrike.current = true;
              }}
              ordersForStrike={ordersForStrike}
              onManageOrders={onManageOrders}
              isManageOrdersPanelOpen={isManageOrdersPanelOpen}
            />

            {/* Current price circle - rendered on top of everything */}
            <CurrentPriceCircle
              x={circleX}
              y={circleY}
              color={colorSpring.color.get()}
            />
          </Group>

          {/* Time range indicator at bottom center */}
          <TimeRangeIndicator
            timeRangeMs={timeRangeMs}
            width={width}
            height={height}
          />
        </ChartAnimations>
      </svg>

      {/* Crosshair recenter button - only show when manually panned */}
      {hasManuallyPanned && (
        <button
          id="chart-recenter-button"
          onClick={() => {
            recenterView();
          }}
          className="absolute bottom-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 active:bg-white/30 border border-white/30 rounded-xl flex items-center justify-center transition-colors"
          style={{
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            position: 'absolute',
            pointerEvents: 'auto',
            touchAction: 'auto',
          }}
          aria-label="Recenter view"
        >
          <FontAwesomeIcon
            icon={faCrosshairs}
            className="text-white"
            style={{ pointerEvents: 'none', fontSize: '20px' }}
          />
        </button>
      )}
    </div>
  );
}
